import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Section } from './Shared';
type Slide = {
  src: string;
  alt: string;
  title: string;
  caption: string;
};
const slides: Slide[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/tcGPb9YhKYM54Dr66Thxjj/333.png',
  alt: '100 Frases Sanadoras — El vínculo materno',
  title: '100 FRASES SANADORAS · EL VÍNCULO MATERNO',
  caption: 'Frases 11–20 listas para sanar el vínculo con la madre, con la nota sistémica de cuándo usar cada una.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/wJUTC5c7W8Z7XcX8sWsgNX/22222.png',
  alt: '100 Preguntas Poderosas — Relaciones de pareja',
  title: '100 PREGUNTAS PODEROSAS · RELACIONES DE PAREJA',
  caption: 'Preguntas 21–30 para abrir la dinámica de pareja, cada una con su lectura sistémica.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/2PoA2SNnHUFTSKW13CRQ5x/Captura_de_pantalla_2026-06-06_023742.png',
  alt: 'Protocolos para culpa, vergüenza y resentimiento',
  title: 'PROTOCOLOS · CULPA, VERGÜENZA Y RESENTIMIENTO',
  caption: 'Las tres emociones que más paralizan el campo, con intervenciones listas para cada una.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/cdQpNAXg9TVmkS39RKLZvF/Captura_de_aaa2026-06-06_023847.png',
  alt: '100 Preguntas Poderosas — Vínculo con el padre',
  title: '100 PREGUNTAS PODEROSAS · VÍNCULO CON EL PADRE',
  caption: 'Preguntas 11–15 sobre el padre, con el "por qué funciona" de cada una para intervenir con precisión.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/9CXgdkSjDEG4kw7CUk6RPR/portada_bono_2_CF.png',
  alt: 'Bono 2 — 100 Frases Sanadoras para Terapeutas',
  title: 'BONO 2 · 100 FRASES SANADORAS PARA TERAPEUTAS™',
  caption: 'La colección completa de frases por vínculo y situación clínica, lista para tu próxima sesión.'
}];
export const MaterialPreview = () => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const goTo = useCallback((next: number) => {
    setDirection(next > index ? 1 : -1);
    setIndex((next + slides.length) % slides.length);
  }, [index]);
  const next = useCallback(() => {
    setDirection(1);
    setIndex(i => (i + 1) % slides.length);
  }, []);
  const prev = useCallback(() => {
    setDirection(-1);
    setIndex(i => (i - 1 + slides.length) % slides.length);
  }, []);
  useEffect(() => {
    if (paused) return;
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [next, paused]);
  // Touch / swipe support for mobile
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(delta) > 40) {
      if (delta < 0) next();else prev();
    }
    setTouchStartX(null);
  };
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 80 : -80,
      opacity: 0,
      scale: 0.96
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -80 : 80,
      opacity: 0,
      scale: 0.96
    })
  };
  return <Section className="max-w-5xl" data-id="element-181" data-mp="1gzrrkp4q8v5c">
      <div className="text-center mb-8 md:mb-12" data-id="element-182" data-mp="1gyqi2p3d0gpq">
        <div className="inline-flex items-center justify-center bg-white/80 border border-brand-gold/40 px-4 py-1.5 rounded-full mb-5 shadow-sm" data-id="element-183" data-mp="1cxwuxxxqr5xw">
          <span className="font-serif font-semibold tracking-wider text-brand-violetDark text-xs md:text-sm" data-id="element-184" data-mp="18051v0ld1ds4">
            VISTA PREVIA DEL VADEMÉCUM
          </span>
        </div>
        <h2 className="text-2xl md:text-4xl lg:text-5xl font-display font-bold leading-tight text-brand-violetDark" data-id="element-185" data-mp="32cl4lrey69s8">
          📸 Conocé el material por dentro
        </h2>
        <p className="text-brand-text/70 mt-4 text-sm md:text-base max-w-2xl mx-auto" data-id="element-186" data-mp="2411q5ztms0pc">
          Libro principal, bonos y fichas de consulta rápida — todo diseñado
          para usar en el consultorio desde la primera sesión.
        </p>
      </div>

      <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-id="element-187" data-mp="1uz10bo0q8gxp">
        <div className="relative rounded-3xl overflow-hidden border border-brand-gold/30 bg-gradient-to-br from-brand-violetDark/95 via-brand-violet/90 to-brand-burgundy/90 shadow-[0_20px_60px_rgba(107,68,35,0.25)]" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-id="element-188" data-mp="1l25zgygze4g9">
          {/* Decorative texture */}
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay pointer-events-none" data-id="element-189" data-mp="xdmscm9o3xpr"></div>

          <div className="relative w-full flex items-center justify-center px-4 md:px-16 py-8 md:py-12" style={{
          minHeight: '420px'
        }} data-id="element-190" data-mp="ipmtcwt06yy2">
            <AnimatePresence custom={direction} mode="wait" data-id="element-191" data-mp="snsnutchd2sw">
              <motion.div key={index} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              x: {
                type: 'spring',
                stiffness: 260,
                damping: 30
              },
              opacity: {
                duration: 0.3
              },
              scale: {
                duration: 0.3
              }
            }} className="flex flex-col items-center justify-center w-full" data-id="element-192" data-mp="slzhzg9oymly">
                <div className="relative" data-id="element-193" data-mp="1gf7xl76l6me6">
                  {/* Soft glow behind image */}
                  <div className="absolute -inset-4 bg-white/10 blur-2xl rounded-3xl" data-id="element-194" data-mp="1ni615yi2bn8p"></div>
                  <img src={slides[index].src} alt={slides[index].alt} draggable={false} className="relative max-h-[320px] md:max-h-[420px] w-auto max-w-full drop-shadow-2xl select-none" data-id="element-195" data-mp="1sgzglwgwtobi" />
                </div>

                <div className="relative mt-5 md:mt-6 text-center max-w-md" data-id="element-196" data-mp="266jf2q3gjzf0">
                  <p className="font-display font-bold text-white text-sm md:text-lg tracking-wide drop-shadow" data-id="element-197" data-mp="2b5mkibnco7ni">
                    {slides[index].title}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <button type="button" onClick={prev} aria-label="Anterior" className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/95 border border-brand-gold/40 shadow-md flex items-center justify-center text-brand-violet hover:bg-white hover:scale-105 active:scale-95 transition-all z-10" data-id="element-198" data-mp="lrhi94y649sy">
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" data-id="element-199" data-mp="2v8jvme6swpj6" />
          </button>
          <button type="button" onClick={next} aria-label="Siguiente" className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/95 border border-brand-gold/40 shadow-md flex items-center justify-center text-brand-violet hover:bg-white hover:scale-105 active:scale-95 transition-all z-10" data-id="element-200" data-mp="2v6fu22wdk2m2">
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" data-id="element-201" data-mp="3e0usnrerxt9d" />
          </button>
        </div>

        {/* Caption below carousel */}
        <div className="mt-5 text-center px-4 min-h-[2.5rem]" data-id="element-202" data-mp="22ke9bn03msh8">
          <AnimatePresence mode="wait" data-id="element-203" data-mp="22hi9tu8lrvqo">
            <motion.p key={index} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.25
          }} className="text-sm md:text-base font-medium text-brand-violetDark" data-id="element-204" data-mp="1xkif2fh7efev">
              {slides[index].caption}
            </motion.p>
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-center gap-2 mt-4" data-id="element-205" data-mp="p7rambxtn7sk">
          {slides.map((_, i) => <button key={i} type="button" onClick={() => goTo(i)} aria-label={`Ir a la imagen ${i + 1}`} className={`h-2 rounded-full transition-all ${i === index ? 'w-7 bg-brand-violet' : 'w-2 bg-brand-violet/20 hover:bg-brand-violet/50'}`} data-id="element-206" data-mp="aj3f25nz6y2m" data-mpi={i} />)}
        </div>
        <p className="text-center text-xs text-brand-muted mt-3" data-id="element-207" data-mp="17zsbfju5p84m">
          {index + 1} / {slides.length} · Deslizá o usá las flechas
        </p>
      </div>
    </Section>;
};